// TODO: Turn this into a modal!

import { forwardRef, ReactNode, useState } from 'react'

import * as React from 'react'
import { Slider } from 'antd'
import Cropper, { CropperProps } from 'react-easy-crop'
import externalService from 'api/externalService'
import getCroppedImg from 'utils/cropImage'
import { Portal } from 'components/Portal'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import { FieldHookConfig } from 'formik/dist/Field'
import { useField } from 'formik'
import { Button } from 'components/Button/Button'
import { ReactComponent as CameraIcon } from 'assets/icons/cameraAdd.svg'
import { ReactComponent as RemoveIcon } from './close.svg'
import Styles from './ImageUploadField.module.scss'
import { useObjectRef } from 'utils/useObjectRef'
import { useField as useInputField } from '@react-aria/label'
import cx from 'classnames'
import { useButton } from '@react-aria/button'
import { AriaButtonProps } from '@react-types/button'
import { useHover } from '@react-aria/interactions'
import { useFocusRing } from '@react-aria/focus'
import { PressEvent } from '@react-types/shared/src/events'
import { getCropperZoom, readImage, cropperImageClass } from 'utils/cropper'
import { reportError } from 'utils/error'

const CROP_DIAMETER = 250

interface Props extends Omit<FileInputProps, 'onChange' | 'onRemove'>, Pick<CropperProps, 'cropSize' | 'cropShape'> {
  onError: (error: string) => void
  onChange: (url: string) => void
}

const FileInputButton = (props: AriaButtonProps) => {
  const ref = useObjectRef<HTMLSpanElement>()
  const { buttonProps } = useButton({ elementType: 'span', ...props }, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <span
      ref={ref}
      {...buttonProps}
      {...hoverProps}
      {...focusProps}
      className={cx(Styles.fileUploadButton, { [Styles.hovered]: isHovered, [Styles.focusVisible]: isFocusVisible })}
    >
      <CameraIcon />
    </span>
  )
}

const FileRemoveButton = (props: AriaButtonProps) => {
  const ref = useObjectRef<HTMLButtonElement>()
  const { buttonProps } = useButton(props, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <button
      ref={ref}
      {...buttonProps}
      {...hoverProps}
      {...focusProps}
      className={cx(Styles.fileRemoveButton, { [Styles.hovered]: isHovered, [Styles.focusVisible]: isFocusVisible })}
    >
      <RemoveIcon />
    </button>
  )
}

interface FileInputProps {
  label: string
  children: ReactNode
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  value: string
  labelContainerClassName?: string
  onRemove: () => void
  useControls?: boolean
}

const FileInput = forwardRef(
  (
    { children, value, onChange, onRemove, labelContainerClassName, useControls = true, ...props }: FileInputProps,
    ref: any,
  ) => {
    const inputRef = useObjectRef<HTMLInputElement>(ref)
    const { labelProps, fieldProps } = useInputField(props)

    const handleFileInputPress = (e: PressEvent) => {
      if (e.pointerType === 'keyboard') {
        inputRef.current?.click()
      }
    }

    const Label = () => {
      // if not using controls render the children as the label (mainly for bc)
      if (!useControls) {
        return <label {...labelProps}>{children}</label>
      }

      return (
        <>
          {children}
          <div className={cx(Styles.labelContainer, labelContainerClassName)}>
            <label {...labelProps} className={Styles.label}>
              <FileInputButton onPress={handleFileInputPress} />
            </label>
            {value && value.length > 0 && (
              <div className={Styles.removeButtonContainer}>
                <FileRemoveButton onPress={onRemove} />
              </div>
            )}
          </div>
        </>
      )
    }

    return (
      <div className={Styles.container}>
        <Label />
        <input
          ref={inputRef}
          {...fieldProps}
          className={Styles.hide}
          type="file"
          onChange={onChange}
          accept={'.png, .jpg, .jpeg'}
        />
      </div>
    )
  },
)

export const ImageUploadField = forwardRef<HTMLInputElement, Props>(
  (
    {
      children,
      value,
      onChange,
      onError,
      labelContainerClassName,
      cropShape = 'rect',
      cropSize = { width: CROP_DIAMETER, height: CROP_DIAMETER },
      useControls = true,
      ...props
    },
    fRef,
  ) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [localPhotoFilePath, setLocalPhotoFilePath] = useState<string | null>(value)
    const [isCropping, setIsCropping] = useState(false)
    const [minZoom, setMinZoom] = useState(0)
    const [maxZoom, setMaxZoom] = useState(0)
    const inputRef = useObjectRef<HTMLInputElement>(fRef)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
      width: number
      height: number
      x: number
      y: number
    }>({ width: 0, height: 0, x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [isZoomSet, setIsZoomSet] = useState(false)

    const uploadFile = async (file: Blob) => {
      try {
        await externalService
          .uploadPhoto(file, cropSize.width, cropSize.height)
          .json(({ secure_url }) => onChange(secure_url))
      } catch (e) {
        reportError(e)
        showToastMessage('There was a problem uploading your image. Please try again later.', 'error')
      }
    }

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target
      const file = files?.item(0)

      if (file) {
        openCropWindow(file)
      } else {
        setLocalPhotoFilePath(null)
      }
    }

    const openCropWindow = (file: Blob) => {
      let reader = new FileReader()

      reader.onload = () => {
        const newFilePath = reader.result

        if (typeof newFilePath === 'string') {
          setLocalPhotoFilePath(newFilePath)
          setIsCropping(true)
        } else if (newFilePath) {
          const stringPath = Buffer.from(newFilePath).toString('utf-8')
          setLocalPhotoFilePath(stringPath)
          setIsCropping(true)
        }
      }

      reader.onerror = () => {
        showToastMessage('There was a problem uploading your image. Please try again later.', 'error')
      }

      readImage(reader, file)
    }

    const onCropChange = (crop: { x: number; y: number }) => {
      if (!isZoomSet) {
        const fitZoom = getCropperZoom(cropSize)

        setIsZoomSet(true)
        setZoom(fitZoom)
        setMinZoom(fitZoom)
        setMaxZoom(fitZoom * 3)
      }

      setCrop(crop)
    }

    const onCropComplete = (
      croppedArea: any,
      croppedAreaPixels: { height: number; width: number; x: number; y: number },
    ) => {
      setCroppedAreaPixels(croppedAreaPixels)
    }

    const onZoomChange = (zoom: number) => {
      setZoom(zoom)
    }

    const onCropCancel = () => {
      setLocalPhotoFilePath(null)
      setIsCropping(false)
      setIsZoomSet(false)
    }

    const handleRemove = () => {
      if (inputRef.current) {
        inputRef.current.value = ''
        setLocalPhotoFilePath(null)
        onChange('')
      }
    }

    const onCropFinish = async () => {
      if (localPhotoFilePath) {
        try {
          const mimeMatch = localPhotoFilePath.match(/^data:image\/(\w+);/)
          const outputFileType = (mimeMatch?.[1] ?? 'jpeg') as 'jpeg' | 'png' | 'webp'

          const croppedImage = (await getCroppedImg(
            localPhotoFilePath,
            croppedAreaPixels,
            { imageQuality: 0.92, outputFileType },
            outputFileType === 'png' ? 'transparent' : '#FFF',
          )) as Blob
          setLocalPhotoFilePath(URL.createObjectURL(croppedImage))
          if (inputRef.current) {
            inputRef.current.value = ''
          }
          uploadFile(croppedImage)
        } catch (e) {
          reportError(e)
          onError("We couldn't process your photo crop, please try again")
        }
      }

      setIsCropping(false)
      setIsZoomSet(false)
    }

    return (
      <>
        {isCropping && localPhotoFilePath && (
          <Portal>
            <div className={Styles.cropControls}>
              <div className={Styles.zoomContainer}>
                <span className={Styles.zoomLabel}>Zoom</span>
                <span className={Styles.icon}>-</span>
                <Slider
                  className={Styles.zoomSlider}
                  value={zoom}
                  min={minZoom}
                  max={maxZoom}
                  step={(maxZoom - minZoom) / 100}
                  tooltipVisible={false}
                  onChange={(e) => onZoomChange(e as number)}
                />
                <span className={Styles.icon}>+</span>
              </div>
              <div className={Styles.buttonContainer}>
                <Button size="md" onPress={onCropCancel} variant="tertiary">
                  Go Back
                </Button>
                <Button size="md" onPress={onCropFinish}>
                  Save
                </Button>
              </div>
            </div>
            <div className={Styles.cropperWrapper}>
              <Cropper
                restrictPosition={false}
                image={localPhotoFilePath}
                crop={crop}
                zoom={zoom}
                minZoom={minZoom}
                maxZoom={maxZoom}
                aspect={1}
                onCropChange={onCropChange}
                onCropComplete={onCropComplete}
                onZoomChange={onZoomChange}
                cropShape={cropShape}
                cropSize={cropSize}
                style={{
                  containerStyle: {},
                  mediaStyle: {},
                  cropAreaStyle: {},
                }}
                classes={{
                  containerClassName: Styles.cropperContainer,
                  mediaClassName: cropperImageClass,
                  cropAreaClassName: '',
                }}
              />
            </div>
          </Portal>
        )}
        <FileInput
          {...props}
          onChange={handleFileInputChange}
          value={value}
          onRemove={handleRemove}
          labelContainerClassName={labelContainerClassName}
          ref={inputRef}
          useControls={useControls}
        >
          {children}
        </FileInput>
      </>
    )
  },
)

interface FormikImageUploadFields extends Pick<CropperProps, 'cropSize' | 'cropShape'> {
  children: (url: string) => ReactNode
  label: string
  labelContainerClassName?: string
}

export const FormikImageUploadField = ({
  children,
  labelContainerClassName,
  onChange,
  ref,
  ...props
}: FieldHookConfig<string> & FormikImageUploadFields) => {
  const [field, , { setValue, setError, setTouched }] = useField(props)

  const handleError = (e: string) => {
    setError(e)
    setTouched(true)
  }

  return (
    <ImageUploadField
      value={field.value}
      labelContainerClassName={labelContainerClassName}
      onChange={setValue}
      {...props}
      onError={handleError}
    >
      {children(field.value)}
    </ImageUploadField>
  )
}
